/**
 * Particles — pooled CPU sprites drawn as camera-facing instanced quads.
 *
 * DRAW PATH (WebGPU): this system used to be a `THREE.Points` with a
 * `PointsMaterial`. Under `WebGPURenderer` that draws NOTHING visible: WebGPU
 * only supports 1-pixel point primitives, so `size`/`sizeAttenuation` are
 * ignored and every burst was invisible even though the simulation was
 * perfectly healthy. The simulation is unchanged; only the drawing changed to
 * two `InstancedMesh`es of 1x1 billboard quads:
 *
 *  - `glow`  — AdditiveBlending: sparks + crash debris (bright, glowy).
 *  - `soft`  — NormalBlending, depthWrite off: dust/smoke (sandy matter, NOT
 *              glowing; this is why the dust palette in juice.ts is now the
 *              true colour instead of the dimmed additive-safe version).
 *
 * Per-instance colour rides `InstancedMesh.instanceColor`; per-instance ALPHA
 * (which plain `MeshBasicMaterial` cannot do) rides a dynamic instanced float
 * attribute wired into a `MeshBasicNodeMaterial.opacityNode`. Billboarding
 * copies the camera's world quaternion into every instance matrix.
 *
 * Effect types, each triggered by a sim event (or, for the slide trail, by the
 * coordinator's per-frame scan):
 *  - kick connect: spark + dust burst at the contact world position.
 *  - kickedVehicleGroundHit: BIG hanging dust/smoke plume at the touchdown
 *    point — the headline effect.
 *  - wreck trail: continuous dark smoke + dark chips + orange sparks + SCRAPE
 *    DUST (sandy road grit at the contact patch) under a wreck skidding along
 *    the ground, at a speed-scaled rate.
 *  - kickedVehicleDown: modest settling puff where the wreck comes to rest.
 *  - crash: glass/debris puff at the player position.
 *
 * Zero steady-state allocation: a fixed-capacity SoA pool is pre-allocated and
 * dead particles are recycled in place; the instance buffers are sized once at
 * construction and only their contents are rewritten per frame.
 */

import {
  Group,
  InstancedMesh,
  InstancedBufferAttribute,
  PlaneGeometry,
  MeshBasicNodeMaterial,
  AdditiveBlending,
  NormalBlending,
  DynamicDrawUsage,
  CanvasTexture,
  Matrix4,
  Quaternion,
  Vector3,
  SRGBColorSpace,
  type Blending,
  type Camera,
} from "three/webgpu";
import { bufferAttribute } from "three/tsl";
import { JUICE } from "./juice";
import { applyEarthCurve } from "./earthCurve";

const P = JUICE.particles;

/** Mutable 3-vector target for {@link sampleBurstDirection} (zero-allocation). */
export interface Dir3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Uniform random direction on the unit sphere from two uniform samples in
 * [0,1). Writes into `out` (no allocation) and always produces a UNIT vector,
 * so the caller's speed scalar is the true radial speed.
 *
 * NOTE (bugfix): the previous inline version multiplied the horizontal
 * components by sin(phi) a second time, so |v| = sp*sqrt(sin^4 phi + cos^2 phi)
 * and bursts collapsed into a vertical spindle instead of spraying radially.
 */
export function sampleBurstDirection(u1: number, u2: number, out: Dir3): void {
  const theta = u1 * Math.PI * 2;
  const cosPhi = 2 * u2 - 1; // uniform in [-1,1] => uniform on the sphere
  const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
  out.x = sinPhi * Math.cos(theta);
  out.y = cosPhi;
  out.z = sinPhi * Math.sin(theta);
}

/**
 * Direction sample for ROADSIDE DUST — an upper hemisphere biased outward,
 * not a full sphere.
 *
 * A dust plume is thrown up and OUT off the ground; a spherical burst sends
 * half its particles straight into the road (where the y-clamp in `update`
 * flattens them into a disc) and reads as an explosion rather than a cloud.
 * Here y is confined to [0, DUST_MAX_COS] so nothing is emitted downward, and
 * because |y| is capped the horizontal component is never below
 * sqrt(1 - DUST_MAX_COS²) — every particle carries real outward speed.
 *
 * Writes into `out` (no allocation); the result is always a UNIT vector, so
 * the caller's speed scalar is the true radial speed.
 */
export const DUST_MAX_COS = 0.85;

export function sampleDustDirection(u1: number, u2: number, out: Dir3): void {
  const theta = u1 * Math.PI * 2;
  const cosPhi = u2 * DUST_MAX_COS; // [0, 0.85] — up and out, never down
  const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
  out.x = sinPhi * Math.cos(theta);
  out.y = cosPhi;
  out.z = sinPhi * Math.sin(theta);
}

const dirScratch: Dir3 = { x: 0, y: 0, z: 0 };

// ---------------------------------------------------------------------------
// Pure life-curve helpers (unit tested)
// ---------------------------------------------------------------------------

/**
 * Per-particle opacity over its life, from the normalised age `t` in [0,1].
 *
 * Both curves fade to exactly 0 at t = 1 so nothing ever pops out of existence.
 * Soft (dust) particles ALSO fade IN over the first few percent of their life —
 * a plume of 52 quads appearing at full alpha in one frame reads as a hard
 * flash. Glow (spark/debris) particles start hot and decay quadratically, which
 * keeps a spark bright for the first half of its short life.
 */
export function particleAlpha(t: number, soft: boolean, chip = false): number {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  const remain = 1 - u;
  if (chip) {
    // Debris chip: near-opaque so a DARK fragment actually reads against the
    // asphalt (a chip at dust alpha is just more grey haze), then a linear fade
    // to nothing. No fade-in — a chip exists the instant it is struck loose.
    return remain * CHIP_MAX_ALPHA;
  }
  if (soft) {
    const fadeIn = u < 0.12 ? u / 0.12 : 1;
    return fadeIn * remain * remain * P.softMaxAlpha;
  }
  return remain * remain;
}

/** Peak opacity of a debris chip (see {@link particleAlpha}). */
export const CHIP_MAX_ALPHA = 0.9;

/**
 * Per-particle world size (metres) over its life, from the base size and the
 * normalised age `t` in [0,1].
 *
 * Dust EXPANDS as it dissipates (a puff billowing outward); sparks/debris
 * SHRINK as they burn out. Both are also part of the fade: the additive glow
 * mesh in particular reads as a dying ember rather than a disappearing square.
 */
export function particleScale(
  size: number,
  t: number,
  soft: boolean,
  chip = false,
): number {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  // A chip is a solid fragment: it never billows. It shrinks slightly as it
  // tumbles away, exactly like the additive debris, even though it is drawn on
  // the normal-blended batch (a dark chip on the additive batch is invisible).
  if (chip) return size * (1.0 - 0.35 * u);
  if (soft) return size * (0.55 + 0.95 * u);
  return size * (1.0 - 0.55 * u);
}

/** Per-particle data stored in parallel typed arrays (SoA for cache efficiency). */
interface ParticlePool {
  // Position (world space).
  px: Float32Array;
  py: Float32Array;
  pz: Float32Array;
  // Velocity (m/s).
  vx: Float32Array;
  vy: Float32Array;
  vz: Float32Array;
  // Lifetime tracking.
  age: Float32Array; // seconds alive
  life: Float32Array; // total lifetime (s)
  // Visual.
  size: Float32Array; // sprite scale (m)
  active: Uint8Array; // 1 = alive, 0 = dead
  /** 1 = soft (normal-blended dust), 0 = glow (additive spark/debris). */
  soft: Uint8Array;
  /** 1 = debris CHIP life curve (opaque, shrinking) — see particleAlpha. */
  chip: Uint8Array;
  // Color (linear 0..1).
  colorR: Float32Array;
  colorG: Float32Array;
  colorB: Float32Array;
  gravity: Float32Array; // per-particle gravity (0 for sparks, g for debris)
}

interface ParticleKindConfig {
  count: number;
  speed: number;
  size: number;
  life: number;
  gravity: number;
  color: [number, number, number] | readonly [number, number, number]; // 0..1
  /**
   * Optional second palette entry. When present each particle picks a random
   * lerp between `color` and `colorAlt`, which is what makes the roadside
   * plume read as mixed sand + road grit instead of one flat tint.
   */
  colorAlt?: readonly [number, number, number];
  /**
   * When true, directions come from `sampleDustDirection` (upper hemisphere,
   * biased outward) instead of the uniform sphere. Sparks/debris keep the
   * sphere — only ground-thrown dust uses the hemisphere.
   */
  hemisphere?: boolean;
  /**
   * When true the particle is drawn on the NORMAL-blended "soft" mesh (dust,
   * smoke: matter that occludes). Otherwise it lands on the ADDITIVE "glow"
   * mesh (sparks, debris glints).
   */
  soft?: boolean;
  /**
   * When true the particle uses the DEBRIS-CHIP life curve (near-opaque,
   * shrinking) instead of the billowing dust curve. Only meaningful together
   * with `soft`, which is the batch a dark fragment has to be drawn on.
   */
  chip?: boolean;
}

/** One instanced draw batch (glow or soft) with its own material + buffers. */
interface Batch {
  mesh: InstancedMesh;
  material: MeshBasicNodeMaterial;
  colors: InstancedBufferAttribute;
  alphas: InstancedBufferAttribute;
  /** Live instance count written this frame. */
  count: number;
}

/**
 * Pooled particle system. Add `root` to the scene; call `burstKick`,
 * `burstDown`, `burstGroundHit`, `puffWreckTrail`, `burstCrash` from event
 * handlers; call `update(dt, camera)` each render frame.
 */
export class Particles {
  /** Scene-graph root holding both instanced batches. Add this to the scene. */
  readonly root: Group;
  private readonly pool: ParticlePool;
  private readonly capacity: number;
  private nextIdx = 0; // round-robin allocation cursor
  private readonly geometry: PlaneGeometry;
  private readonly sprite: CanvasTexture;
  private readonly glow: Batch;
  private readonly soft: Batch;

  // Scratch objects for matrix composition (zero per-frame allocation).
  private readonly tmpMatrix = new Matrix4();
  private readonly tmpPos = new Vector3();
  private readonly tmpScale = new Vector3();
  private readonly tmpQuat = new Quaternion();

  constructor() {
    this.capacity = P.poolSize;
    this.pool = this.createPool();

    // One shared unit quad. Both batches instance the same geometry; it is
    // disposed once in dispose().
    this.geometry = new PlaneGeometry(1, 1);
    this.sprite = this.makeSpriteTexture();

    this.glow = this.makeBatch("particles-glow", AdditiveBlending);
    this.soft = this.makeBatch("particles-soft", NormalBlending);

    this.root = new Group();
    this.root.name = "particles";
    this.root.frustumCulled = false;
    this.root.add(this.glow.mesh, this.soft.mesh);
  }

  // --- Burst triggers (called from event handlers) ---

  /**
   * Spark + dust burst at a kick contact point (world space).
   *
   * Fired ONCE, from the sim's `kick` event, at that event's contact point —
   * never tracked per frame — so the puff is pinned to where the boot met the
   * bodywork. The dust is drawn larger than the generic kind (P.kickDustSize):
   * this is the only burst that happens METRES away from the camera.
   */
  burstKick(x: number, y: number, z: number): void {
    this.spawn({
      ...this.kindConfig("spark"),
      count: P.kickSparkCount,
    }, x, y, z);
    this.spawn({
      ...this.kindConfig("dust"),
      count: P.kickDustCount,
      size: P.kickDustSize,
    }, x, y, z);
  }

  /**
   * Modest settling puff when a kicked vehicle finally comes to REST off-road
   * (`kickedVehicleDown`). Deliberately smaller than the impact plume — the
   * big cloud belongs to the moment of touchdown, not to the wreck stopping.
   */
  burstDown(x: number, z: number): void {
    this.spawn({
      ...this.roadDustConfig(),
      count: P.downDustCount,
      speed: P.groundHitSpeed * 0.5,
      size: P.groundHitSize * 0.7,
      life: P.groundHitLifetime * 0.8,
    }, x, P.groundHitY, z);
  }

  /**
   * BIG dust/smoke plume at a kicked vehicle's first ground impact
   * (`kickedVehicleGroundHit`). This is the headline effect: a wide, slow,
   * hanging cloud thrown up and outward the instant the wreck touches down.
   */
  burstGroundHit(x: number, z: number): void {
    this.spawn({
      ...this.roadDustConfig(),
      count: P.groundHitCount,
    }, x, P.groundHitY, z);
  }

  /**
   * One emission of the WRECK TRAIL: thick dark smoke, struck-loose debris
   * (dark chips + orange sparks) and SCRAPE DUST from a wreck skidding along
   * the road.
   *
   * Called repeatedly while the wreck is grounded and above
   * `JUICE.particles.trailMinSpeed`, at a rate the coordinator scales with the
   * wreck's live speed, and at most `trailMaxPerFrame` times per frame so a
   * long multi-wreck skid can never drain the pool out from under the landing
   * plumes. `x`/`z` are the wreck's world position; the three layers place
   * themselves in Y (smoke at body height, debris at the road surface).
   *
   * @param bodyY the wreck's live body-centre height (m). A tumbling wreck
   *   rides on its corners metres above its resting height, so the smoke is
   *   placed relative to the BODY (clamped into `wreckSmokeYMin..wreckSmokeYMax`)
   *   rather than at a fixed height — pinned to the road it detached visibly
   *   from the wreck it is supposed to be pouring off. Debris stays at the road
   *   surface, which is where the contact actually is.
   * @param speedFrac 0..1 — how fast the wreck is relative to `trailSpeedRef`.
   *   Scales the debris' ejection speed so a slow wreck throws lazy chips and a
   *   fast one sprays them. Smoke is deliberately NOT scaled: it billows from a
   *   hot, torn body at the same rate whatever the skid speed.
   */
  puffWreckTrail(x: number, z: number, bodyY: number, speedFrac: number): void {
    const f = speedFrac < 0 ? 0 : speedFrac > 1 ? 1 : speedFrac;
    const smokeY = Math.min(
      P.wreckSmokeYMax,
      Math.max(P.wreckSmokeYMin, bodyY * P.wreckSmokeYFrac),
    );
    // 1. SMOKE — big, dark, buoyant (negative gravity), soft batch.
    this.spawn({
      count: P.wreckSmokeCount,
      speed: P.wreckSmokeSpeed,
      size: P.wreckSmokeSize,
      life: P.wreckSmokeLifetime,
      gravity: P.wreckSmokeGravity,
      color: P.smokeColorDark,
      colorAlt: P.smokeColorGrey,
      hemisphere: true,
      soft: true,
    }, x, smokeY, z);
    // 2. CHIPS — small dark ballistic fragments, soft batch + chip life curve.
    this.spawn({
      count: P.wreckChipCount,
      speed: P.wreckChipSpeed * (0.45 + 0.55 * f),
      size: P.wreckChipSize,
      life: P.wreckChipLifetime,
      gravity: P.gravity,
      color: P.chipColorDark,
      colorAlt: P.chipColorRust,
      soft: true,
      chip: true,
    }, x, P.wreckDebrisY, z);
    // 3. SPARKS — hot additive glints struck off the asphalt.
    this.spawn({
      count: P.wreckSparkCount,
      speed: P.wreckSparkSpeed * (0.45 + 0.55 * f),
      size: P.wreckSparkSize,
      life: P.wreckSparkLifetime,
      gravity: P.gravity,
      color: P.sparkColorHot,
    }, x, P.wreckDebrisY, z);
    // 4. SCRAPE DUST — sandy road grit ploughed up at the contact patch. Same
    // palette, batch and material as the impact plume (`roadDustConfig`), so it
    // introduces no new pipeline: it is that config at trail scale, emitted at
    // the ROAD SURFACE rather than at body height. Without it the trail was all
    // dark smoke and dark chips, and a wreck grinding along the asphalt left a
    // black smear with no ground contact read. Speed-scaled like the debris.
    this.spawn({
      ...this.roadDustConfig(),
      count: P.wreckScrapeCount,
      speed: P.wreckScrapeSpeed * (0.5 + 0.5 * f),
      size: P.wreckScrapeSize,
      life: P.wreckScrapeLifetime,
      gravity: P.wreckScrapeGravity,
    }, x, P.wreckScrapeY, z);
  }

  /** Glass/debris puff at the player position on crash. */
  burstCrash(x: number, y: number, z: number): void {
    this.spawn({
      ...this.kindConfig("debris"),
      count: P.crashDebrisCount,
    }, x, y, z);
  }

  /**
   * Advance all live particles by `dt` seconds and rebuild the instance
   * buffers. `camera` (when given) supplies the billboard orientation — every
   * quad is rotated by the camera's WORLD quaternion so it always faces the
   * viewer; without a camera the quads keep their last orientation.
   */
  update(dt: number, camera?: Camera): void {
    const pool = this.pool;
    if (camera) {
      camera.getWorldQuaternion(this.tmpQuat);
    }
    const quat = this.tmpQuat;
    this.glow.count = 0;
    this.soft.count = 0;

    for (let i = 0; i < this.capacity; i++) {
      if (!pool.active[i]) continue;
      pool.age[i] += dt;
      if (pool.age[i] >= pool.life[i]) {
        pool.active[i] = 0;
        continue;
      }
      // Physics: velocity + gravity.
      pool.vy[i] -= pool.gravity[i] * dt;
      pool.px[i] += pool.vx[i] * dt;
      pool.py[i] += pool.vy[i] * dt;
      pool.pz[i] += pool.vz[i] * dt;
      // Ground clamp (don't sink below y=0).
      if (pool.py[i] < 0.02) {
        pool.py[i] = 0.02;
        pool.vy[i] = 0;
        pool.vx[i] *= 0.8;
        pool.vz[i] *= 0.8;
      }

      const isSoft = pool.soft[i] === 1;
      const isChip = pool.chip[i] === 1;
      const batch = isSoft ? this.soft : this.glow;
      const w = batch.count;
      if (w >= this.capacity) continue; // can't happen (both share the pool)
      const t = pool.age[i] / pool.life[i];

      // Billboard instance matrix: camera rotation, life-curve scale, world pos.
      const s = particleScale(pool.size[i], t, isSoft, isChip);
      this.tmpPos.set(pool.px[i], pool.py[i], pool.pz[i]);
      this.tmpScale.set(s, s, s);
      this.tmpMatrix.compose(this.tmpPos, quat, this.tmpScale);
      this.tmpMatrix.toArray(batch.mesh.instanceMatrix.array as Float32Array, w * 16);

      const colors = batch.colors.array as Float32Array;
      colors[w * 3] = pool.colorR[i];
      colors[w * 3 + 1] = pool.colorG[i];
      colors[w * 3 + 2] = pool.colorB[i];
      (batch.alphas.array as Float32Array)[w] = particleAlpha(t, isSoft, isChip);
      batch.count = w + 1;
    }

    this.flush(this.glow);
    this.flush(this.soft);
  }

  reset(): void {
    const pool = this.pool;
    for (let i = 0; i < this.capacity; i++) {
      pool.active[i] = 0;
    }
    this.glow.count = 0;
    this.soft.count = 0;
    this.glow.mesh.count = 0;
    this.soft.mesh.count = 0;
  }

  dispose(): void {
    this.geometry.dispose();
    this.sprite.dispose();
    this.glow.material.dispose();
    this.soft.material.dispose();
    this.glow.mesh.dispose();
    this.soft.mesh.dispose();
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /** Push this frame's instance data to the GPU and set the draw count. */
  private flush(batch: Batch): void {
    batch.mesh.count = batch.count;
    if (batch.count === 0) return;
    batch.mesh.instanceMatrix.needsUpdate = true;
    batch.colors.needsUpdate = true;
    batch.alphas.needsUpdate = true;
  }

  /**
   * Build one instanced batch. Per-instance colour uses the standard
   * `instanceColor` attribute (NodeMaterial multiplies it into the diffuse);
   * per-instance ALPHA — which no stock material exposes — is a dynamic
   * instanced float attribute bound to `opacityNode`, multiplied by the sprite
   * texture's own radial alpha so quads read as round puffs, not squares.
   */
  private makeBatch(name: string, blending: Blending): Batch {
    const cap = this.capacity;
    const material = new MeshBasicNodeMaterial({
      map: this.sprite,
      transparent: true,
      blending,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });
    const alphas = new InstancedBufferAttribute(new Float32Array(cap), 1);
    alphas.setUsage(DynamicDrawUsage);
    material.opacityNode = bufferAttribute<"float">(alphas, "float");

    // Particles are WORLD-space billboards, so they take the curved-earth bend
    // like everything else (render/earthCurve.ts). They live within a few metres
    // of the bike, where the drop is under a centimetre — this is for
    // consistency (a wreck's smoke must not float off a sinking wreck at 60 m),
    // not because it is visible up close.
    applyEarthCurve(material);

    const mesh = new InstancedMesh(this.geometry, material, cap);
    mesh.name = name;
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    const colors = new InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    colors.setUsage(DynamicDrawUsage);
    mesh.instanceColor = colors;

    return { mesh, material, colors, alphas, count: 0 };
  }

  private createPool(): ParticlePool {
    const n = this.capacity;
    return {
      px: new Float32Array(n),
      py: new Float32Array(n),
      pz: new Float32Array(n),
      vx: new Float32Array(n),
      vy: new Float32Array(n),
      vz: new Float32Array(n),
      age: new Float32Array(n),
      life: new Float32Array(n),
      size: new Float32Array(n),
      active: new Uint8Array(n),
      soft: new Uint8Array(n),
      chip: new Uint8Array(n),
      colorR: new Float32Array(n),
      colorG: new Float32Array(n),
      colorB: new Float32Array(n),
      gravity: new Float32Array(n),
    };
  }

  private kindConfig(kind: "spark" | "dust" | "debris"): ParticleKindConfig {
    if (kind === "spark") {
      return {
        count: 0,
        speed: P.sparkSpeed,
        size: P.sparkSize,
        life: P.sparkLifetime,
        gravity: 0,
        // Deliberately >1: the glow mesh is additive and NOT tone mapped, so
        // over-bright values clip to a hot white core with a gold falloff,
        // which is what makes a spark read as a spark rather than a mustard
        // dot.
        color: [1.7, 1.15, 0.4],
      };
    }
    if (kind === "dust") {
      return {
        count: 0,
        speed: P.dustSpeed,
        size: P.dustSize,
        life: P.dustLifetime,
        gravity: 1.5,
        color: P.dustColorTan,
        colorAlt: P.dustColorGrey,
        soft: true,
      };
    }
    // debris
    return {
      count: 0,
      speed: P.debrisSpeed,
      size: P.debrisSize,
      life: P.debrisLifetime,
      gravity: P.gravity,
      color: [0.9, 0.9, 0.95],
    };
  }

  /**
   * Base config for every ROADSIDE dust effect (impact plume, slide trail,
   * settling puff): sandy-grey two-tone palette, big soft sprites, near-zero
   * gravity so the cloud hangs, upper-hemisphere directions, drawn on the
   * NORMAL-blended soft mesh.
   */
  private roadDustConfig(): ParticleKindConfig {
    return {
      count: 0,
      speed: P.groundHitSpeed,
      size: P.groundHitSize,
      life: P.groundHitLifetime,
      gravity: P.groundHitGravity,
      color: P.dustColorTan,
      colorAlt: P.dustColorGrey,
      hemisphere: true,
      soft: true,
    };
  }

  private spawn(cfg: ParticleKindConfig, x: number, y: number, z: number): void {
    const pool = this.pool;
    const count = cfg.count;
    for (let i = 0; i < count; i++) {
      const idx = this.nextIdx;
      this.nextIdx = (this.nextIdx + 1) % this.capacity;
      // Random direction: uniform over the sphere for sparks/debris, upper
      // hemisphere biased outward for ground-thrown dust.
      if (cfg.hemisphere) {
        sampleDustDirection(Math.random(), Math.random(), dirScratch);
      } else {
        sampleBurstDirection(Math.random(), Math.random(), dirScratch);
      }
      const sp = cfg.speed * (0.5 + Math.random() * 0.5);
      pool.vx[idx] = dirScratch.x * sp;
      pool.vy[idx] = dirScratch.y * sp;
      pool.vz[idx] = dirScratch.z * sp;
      pool.px[idx] = x;
      pool.py[idx] = y;
      pool.pz[idx] = z;
      pool.age[idx] = 0;
      pool.life[idx] = cfg.life * (0.7 + Math.random() * 0.6);
      pool.size[idx] = cfg.size * (0.7 + Math.random() * 0.6);
      pool.active[idx] = 1;
      pool.soft[idx] = cfg.soft ? 1 : 0;
      pool.chip[idx] = cfg.chip ? 1 : 0;
      const alt = cfg.colorAlt;
      if (alt) {
        // Two-tone palette: each particle sits somewhere between the two tints.
        const m = Math.random();
        pool.colorR[idx] = cfg.color[0] + (alt[0] - cfg.color[0]) * m;
        pool.colorG[idx] = cfg.color[1] + (alt[1] - cfg.color[1]) * m;
        pool.colorB[idx] = cfg.color[2] + (alt[2] - cfg.color[2]) * m;
      } else {
        pool.colorR[idx] = cfg.color[0];
        pool.colorG[idx] = cfg.color[1];
        pool.colorB[idx] = cfg.color[2];
      }
      pool.gravity[idx] = cfg.gravity;
    }
  }

  private makeSpriteTexture(): CanvasTexture {
    const px = P.spritePx;
    const canvas = document.createElement("canvas");
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext("2d")!;
    const cx = px / 2;
    const r = px / 2;
    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, r);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.45, "rgba(255,255,255,0.6)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, px, px);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }
}
